import React from 'react';
import { Button, Checkbox, Input, Slider } from '@kaleyra/emmy';
import { useTranslation } from 'react-i18next';
import { DynamicVar } from './CallbackProfileCreate';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';
import Styles from '../CallbackProfile.module.scss';

type EditDynamicVariablesProps = {
	visible: boolean;
	onClose: () => void;
	dynamicVariables: DynamicVar[];
	queryString: boolean;
};

export const ListDynamicVariables: React.FC<EditDynamicVariablesProps> = ({
	visible,
	onClose,
	dynamicVariables,
	queryString,
}) => {
	const { t } = useTranslation();
	const [search, setSearch] = React.useState('');
	const [picked, setPicked] = React.useState<string[]>([]);

	// Remove scrollbar
	React.useEffect(() => {
		if (!visible) {
			document.body.setAttribute(
				'style',
				`position: relative; width: calc(100% - ${
					window.innerWidth - document.body.offsetWidth
				}px); overflow: hidden;`
			);
		}
	}, [visible]);

	const filteredDynamicVariables = dynamicVariables.filter(
		(dynamicVariable) => {
			const searchLowered = search.toLowerCase();
			const includesName = dynamicVariable.value
				.toLowerCase()
				.includes(searchLowered);
			const includesDescription = dynamicVariable.description
				.toLowerCase()
				.includes(searchLowered);

			return includesName || includesDescription;
		}
	);

	const handleChackboxChange = (e: CheckboxChangeEvent) => {
		const { name, checked } = e.target;

		const pickedOne = dynamicVariables.find(
			(variable) => variable.value === name
		);

		if (!pickedOne) return;

		setPicked((previous) => {
			const index = previous.findIndex((item) => item === name);

			// Not found but not checked
			if (index === -1 && !checked) return previous;
			// Not found but checked
			if (index === -1 && checked) return [...previous, pickedOne.value];

			// Found but not checked
			return previous.filter((item) => item !== name);
		});
	};

	const everythingSelected = picked.length === dynamicVariables.length;

	const handleSelect = () => {
		if (everythingSelected) return setPicked([]);
		return setPicked(dynamicVariables.map((data) => data.value));
	};

	const copyContent = () => {
		const record = getSelectedObject(picked, queryString);

		window.navigator?.clipboard.writeText(record);
	};

	return (
		<Slider
			visibility={visible}
			title={t('addDynamicVar')}
			onClose={onClose}
		>
			<Input
				value={search}
				onChange={(e) => setSearch(e.target.value)}
				label={t('mainSearch')}
				placeholder={t('mainSearch')}
				className={Styles.filterDynamicVariables}
				showBottomLabelSpace={false}
			/>

			<div className={Styles.selectAllButton}>
				<Button
					type="primary"
					onClick={copyContent}
					iconPosition="left"
					small
				>
					{t('mainCopyToClipboard')}
				</Button>

				<Button type="link" onClick={handleSelect}>
					{everythingSelected
						? t('mainDeselectAll')
						: t('mainSelectAll')}
				</Button>
			</div>

			{filteredDynamicVariables.map((dynamicVariable) => (
				<Checkbox
					description={dynamicVariable.description}
					label={dynamicVariable.value}
					onChange={handleChackboxChange}
					name={dynamicVariable.value}
					key={dynamicVariable.value}
					checked={Boolean(
						picked.find(
							(variable) => variable === dynamicVariable.value
						)
					)}
				/>
			))}
		</Slider>
	);
};

function getSelectedObject(variables: string[], isQS: boolean): string {
	if (isQS)
		return variables.reduce((accumulator, current) => {
			// We are at the beginning of the loop
			let prefix = accumulator.length === 1 ? '' : '&';
			return `${accumulator}${prefix}${current}=<${current}>`;
		}, '?');

	const object: Record<string, string> = {};
	for (const key of variables) {
		object[key] = `<${key}>`;
	}

	return JSON.stringify(object, null, 2);
}
